import { useParams, Link, useNavigate } from 'react-router-dom'
import { useWork, useUpdateWork, useReassignComposer } from '@/hooks/useWorks'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { ConfidenceBadge } from '@/components/shared/ConfidenceBadge'
import { QualityDropdown } from '@/components/shared/QualityDropdown'
import { WikidataLink } from '@/components/shared/WikidataLink'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { Button } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowLeft } from 'lucide-react'
import type { QualityScore } from '@brent/shared/types'

export function WorkDetail() {
  const { id } = useParams()
  const workId = Number(id)
  const navigate = useNavigate()
  const { data, isLoading, error } = useWork(workId)
  const { canEdit } = useAuth()
  const updateWork = useUpdateWork()
  const reassignComposer = useReassignComposer()
  const { toast } = useToast()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading work...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
        <h2 className="font-semibold text-destructive">Failed to load work</h2>
        <p className="mt-1 text-sm text-muted-foreground">{error.message}</p>
      </div>
    )
  }

  if (!data) return null

  const { work, performances } = data

  const handleQualityChange = async (qualityScore: QualityScore) => {
    try {
      await updateWork.mutateAsync({
        id: work.id,
        input: { quality_score: qualityScore },
      })
      toast({ title: 'Updated', description: `Quality set to ${qualityScore}` })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update quality',
      })
    }
  }

  const handleQidSave = async (qid: string) => {
    try {
      const result = await updateWork.mutateAsync({
        id: work.id,
        input: { wikidata_id: qid },
      })
      if (result.enriched) {
        toast({
          title: 'Enriched',
          description: `Matched to ${result.enriched.label}`,
        })
      } else if (result.cleared) {
        toast({ title: 'Cleared', description: 'Wikidata ID removed' })
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update QID',
      })
    }
  }

  const handleComposerReassign = async (value: string) => {
    try {
      const result = await reassignComposer.mutateAsync({
        workId: work.id,
        input: { value },
      })
      if (result.merged_into) {
        toast({
          title: 'Merged',
          description: `Work merged into #${result.merged_into}`,
        })
        navigate(`/works/${result.merged_into}`)
        return
      }
      if (result.unlinked) {
        toast({ title: 'Unlinked', description: 'Composer removed from work' })
      } else if (result.created) {
        toast({
          title: 'Reassigned',
          description: `New composer created: ${result.composer_name}`,
        })
      } else {
        toast({
          title: 'Reassigned',
          description: `Composer set to ${result.composer_name}`,
        })
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to reassign composer',
      })
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <Link to="/works">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to works
          </Button>
        </Link>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{work.title_canonical}</h1>
            {work.title_short && <p className="mt-1 text-muted-foreground">{work.title_short}</p>}
          </div>
          <div className="flex items-center gap-3">
            <ConfidenceBadge confidence={work.confidence} qualityScore={work.quality_score} />
            {canEdit ? (
              <QualityDropdown
                value={work.quality_score}
                onChange={handleQualityChange}
                disabled={updateWork.isPending}
              />
            ) : (
              <Badge variant="secondary" className="capitalize">
                {work.quality_score.replace('_', ' ')}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Metadata */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Details</h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Composer</dt>
            <dd className="mt-1">
              {canEdit ? (
                <InlineEdit
                  value={work.composer_name || ''}
                  onSave={handleComposerReassign}
                  placeholder="Add composer (name or QID)"
                />
              ) : work.composer_name ? (
                <Link
                  to={`/composers/${work.composer_id}`}
                  className="text-sm text-primary hover:underline"
                >
                  {work.composer_name}
                </Link>
              ) : (
                <span className="text-sm text-muted-foreground">-</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Wikidata ID</dt>
            <dd className="mt-1">
              {canEdit ? (
                <InlineEdit
                  value={work.wikidata_id || ''}
                  onSave={handleQidSave}
                  placeholder="Add QID (e.g. Q12345)"
                />
              ) : (
                <WikidataLink qid={work.wikidata_id} />
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Wikidata Label</dt>
            <dd className="mt-1 text-sm">{work.wikidata_label || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Programme Appearances</dt>
            <dd className="mt-1 text-sm tabular-nums">{work.programme_count}</dd>
          </div>
          {work.parent_work_qid && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Part of</dt>
              <dd className="mt-1">
                <WikidataLink qid={work.parent_work_qid} />
              </dd>
            </div>
          )}
        </dl>
      </section>

      {/* Performance history */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">
          Performance History{' '}
          <span className="text-muted-foreground font-normal">({performances.length})</span>
        </h2>
        {performances.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Venue</TableHead>
                  <TableHead>Ensemble</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {performances.map((perf) => (
                  <TableRow
                    key={`${perf.concert_id}-${perf.position}`}
                    className="cursor-pointer"
                    onClick={() => navigate(`/concerts/${perf.concert_id}`)}
                  >
                    <TableCell className="whitespace-nowrap">
                      {perf.date_iso || perf.date_text || `Concert #${perf.concert_id}`}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {perf.venue_name || '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {perf.ensemble_name || '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {perf.arrangement_note || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No performances recorded.</p>
        )}
      </section>
    </div>
  )
}
